import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import { PLATFORM_ACCENTS } from '@/lib/format';
import { Cookie, Info, Sparkles } from 'lucide-react';

import { PLATFORM_HELP } from '@/components/admin/platforms/helpers';
import { PlatformStatusHeader } from '@/components/admin/platforms/PlatformStatusHeader';
import { CookieDropZone } from '@/components/admin/platforms/CookieDropZone';
import { PlatformAdvancedForm } from '@/components/admin/platforms/PlatformAdvancedForm';
import { DiscoveryDialog } from '@/components/admin/platforms/DiscoveryDialog';
import { CurlImportDialog } from '@/components/admin/platforms/CurlImportDialog';

function HowToGuide({ platformName, loginUrl, site }) {
    return (
        <details className="rounded-xl border border-border bg-white p-3 text-xs">
            <summary className="cursor-pointer font-medium flex items-center gap-1.5 select-none">
                <Info className="h-3.5 w-3.5 text-[#0E2A47]" /> How to export cookies.txt (no tech knowledge needed)
            </summary>
            <ol className="list-decimal pl-5 mt-2 space-y-1 text-foreground/80">
                <li>
                    Install the free Chrome extension{' '}
                    <a className="text-[#1F9D8A] underline" target="_blank" rel="noreferrer"
                        href="https://chrome.google.com/webstore/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc">
                        "Get cookies.txt LOCALLY"
                    </a>.
                </li>
                <li>
                    Open the {platformName} portal and log in:{' '}
                    <a className="text-[#1F9D8A] underline" target="_blank" rel="noreferrer" href={loginUrl || site}>
                        {loginUrl || site}
                    </a>
                </li>
                <li>Click the extension icon &rarr; <strong>Export</strong>. You'll get a file called <code>cookies.txt</code>.</li>
                <li>Drag and drop that file into the box above. Done.</li>
            </ol>
        </details>
    );
}

function StoredCookiesPreview({ count, preview }) {
    if (!preview || preview.length === 0) return null;
    return (
        <details className="rounded-xl border border-border bg-white p-3 text-xs">
            <summary className="cursor-pointer font-medium flex items-center gap-1.5 select-none">
                <Sparkles className="h-3.5 w-3.5 text-[#0E2A47]" /> Stored cookies ({count}) &mdash; values masked
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2 max-h-44 overflow-y-auto pr-1">
                {preview.map((c) => (
                    <div key={c.name} className="text-[11px] flex items-center gap-2 bg-secondary/40 px-2 py-1 rounded">
                        <code className="font-mono font-semibold text-foreground/80 truncate max-w-[140px]">{c.name}</code>
                        <span className="text-muted-foreground truncate">{c.value_masked}</span>
                        {c.domain && <span className="text-muted-foreground/60 truncate ml-auto">{c.domain}</span>}
                    </div>
                ))}
            </div>
        </details>
    );
}

export default function AdminPlatformsPage() {
    const [list, setList] = useState([]);
    const [active, setActive] = useState('trumeet');
    const [forms, setForms] = useState({});
    const [testing, setTesting] = useState({});
    const [uploading, setUploading] = useState({});
    const [dragOver, setDragOver] = useState(null);
    const [discovering, setDiscovering] = useState(false);
    const [discoverResult, setDiscoverResult] = useState(null);
    const [discoverOpen, setDiscoverOpen] = useState(false);
    const [curlOpen, setCurlOpen] = useState(false);
    const [curlPlatform, setCurlPlatform] = useState(null);
    const [curlText, setCurlText] = useState('');
    const [curlInAppId, setCurlInAppId] = useState('');
    const [curlAmount, setCurlAmount] = useState('');
    const [curlImporting, setCurlImporting] = useState(false);
    const [curlResult, setCurlResult] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState({});

    const load = useCallback(async () => {
        const { data } = await api.get('/admin/platforms/cookies');
        setList(data.platforms || []);
        const f = {};
        for (const p of data.platforms) {
            f[p.platform] = {
                headers_json: p.headers_json,
                endpoint_template: p.endpoint_template,
                payload_template: p.payload_template,
                test_url: p.test_url,
                discovery_url: p.discovery_url,
                method: p.method,
                mode: p.mode || 'api',
            };
        }
        setForms(f);
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateField = useCallback((platform, key, value) => {
        setForms((prev) => ({ ...prev, [platform]: { ...(prev[platform] || {}), [key]: value } }));
    }, []);

    const save = async (platform) => {
        try {
            await api.put(`/admin/platforms/${platform}/cookies`, { ...forms[platform], cookies: '' });
            toast.success(`${platform} configuration saved`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        }
    };

    const test = async (platform) => {
        setTesting((prev) => ({ ...prev, [platform]: true }));
        try {
            const { data } = await api.post(`/admin/platforms/${platform}/test`);
            const r = data.result;
            if (r.status === 'CONNECTED') toast.success(`${platform}: Connected · ${r.preview || r.message}`);
            else toast.warning(`${platform}: ${r.status} - ${r.message}`);
            load();
        } catch (e) {
            toast.error('Test failed');
        } finally { setTesting((prev) => ({ ...prev, [platform]: false })); }
    };

    const discover = async (platform) => {
        const f = forms[platform] || {};
        setDiscovering(true);
        setDiscoverResult(null);
        setDiscoverOpen(true);
        try {
            const { data } = await api.post(`/admin/platforms/${platform}/discover`, {
                discovery_url: f.discovery_url || '',
            });
            setDiscoverResult({ platform, ...(data.result || {}) });
            const count = (data.result?.candidate_endpoints || []).length;
            if (count > 0) toast.success(`Discovery complete: ${count} candidate endpoints found`);
            else toast.warning('Discovery ran but found 0 candidate endpoints. Cookies may be expired or page requires login.');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Discovery failed');
            setDiscoverOpen(false);
        } finally { setDiscovering(false); }
    };

    const applyEndpoint = (platform, url) => {
        setForms((prev) => ({ ...prev, [platform]: { ...(prev[platform] || {}), endpoint_template: url } }));
        toast.success('Applied. Remember to click "Save" to persist.');
        setDiscoverOpen(false);
    };

    const openCurlImport = (platform) => {
        setCurlPlatform(platform);
        setCurlText('');
        setCurlInAppId('');
        setCurlAmount('');
        setCurlResult(null);
        setCurlOpen(true);
    };

    const importCurl = async () => {
        if (!curlText.trim()) { toast.error('Paste a cURL command first'); return; }
        setCurlImporting(true);
        try {
            const { data } = await api.post(`/admin/platforms/${curlPlatform}/import-curl`, {
                curl: curlText, in_app_id: curlInAppId, amount: curlAmount,
            });
            setCurlResult(data);
            toast.success('cURL imported. Platform bridge auto-configured.');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
        finally { setCurlImporting(false); }
    };

    const uploadFile = async (platform, file) => {
        if (!file) return;
        const allowedExt = /\.(txt|cookies?)$/i;
        if (!allowedExt.test(file.name)) {
            const proceed = window.confirm(`File "${file.name}" doesn't look like a cookies.txt. Try to upload anyway?`);
            if (!proceed) return;
        }
        setUploading((prev) => ({ ...prev, [platform]: true }));
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await axios.post(`${API_BASE}/admin/platforms/${platform}/cookies/upload`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });
            const at = data.auto_test;
            if (at?.status === 'CONNECTED') {
                toast.success(`✅ ${platform}: ${data.count} cookies saved · ${at.preview || 'connected'}`);
            } else if (at) {
                toast.warning(`⚠️ ${data.count} cookies saved, but session test: ${at.status} (${at.message || ''})`);
            } else {
                toast.success(`Uploaded ${data.count} cookies from ${data.filename}`);
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally { setUploading((prev) => ({ ...prev, [platform]: false })); }
    };

    const clearCookies = async (platform) => {
        if (!window.confirm(`Clear all stored cookies for ${platform}?`)) return;
        try { await api.delete(`/admin/platforms/${platform}/cookies`); toast.success('Cookies cleared'); load(); }
        catch (e) { toast.error('Failed to clear cookies'); }
    };

    const onDrop = (e, platform) => {
        e.preventDefault();
        setDragOver(null);
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(platform, file);
    };

    return (
        <AdminShell title="Platform Bridge">
            <Card className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-1">
                    <Cookie className="h-4 w-4 text-[#0E2A47]" />
                    <div className="font-display font-semibold">Platform sessions</div>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                    When your session expires, just drop the new <code className="bg-secondary px-1 rounded text-[11px]">cookies.txt</code> file below
                    for the platform &mdash; everything else (endpoints, signing, headers) is handled automatically for natively-supported platforms.
                </p>

                <Tabs value={active} onValueChange={setActive} data-testid="admin-platform-cookies-tabs">
                    <TabsList>
                        {list.map((p) => (
                            <TabsTrigger key={p.platform} value={p.platform} data-testid={`admin-platform-tab-${p.platform}`}>
                                <span className="flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full" style={{ background: PLATFORM_ACCENTS[p.platform]?.color }} />
                                    {p.name}
                                    {p.is_native && (
                                        <Badge variant="secondary" className="ml-1 text-[9px] font-normal px-1 py-0 bg-emerald-100 text-emerald-800 border-emerald-200">
                                            native
                                        </Badge>
                                    )}
                                </span>
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {list.map((p) => {
                        const help = PLATFORM_HELP[p.platform] || {};
                        const hasCookies = (p.cookies_count || 0) > 0;
                        const showAdvanced = !p.is_native || !!advancedOpen[p.platform];

                        return (
                            <TabsContent key={p.platform} value={p.platform} className="mt-4 space-y-4">
                                <PlatformStatusHeader platform={p} />

                                <CookieDropZone
                                    platform={p}
                                    isUploading={!!uploading[p.platform]}
                                    isTesting={!!testing[p.platform]}
                                    hasCookies={hasCookies}
                                    dragOverPlatform={dragOver}
                                    onDragOver={setDragOver}
                                    onDragLeave={() => setDragOver(null)}
                                    onDrop={onDrop}
                                    onUploadFile={uploadFile}
                                    onTest={test}
                                    onClear={clearCookies}
                                />

                                <HowToGuide platformName={p.name} loginUrl={help.login_url} site={help.site} />

                                {hasCookies && <StoredCookiesPreview count={p.cookies_count} preview={p.cookies_preview} />}

                                {showAdvanced ? (
                                    <PlatformAdvancedForm
                                        platform={p}
                                        form={forms[p.platform]}
                                        onUpdate={updateField}
                                        onSave={save}
                                        onDiscover={discover}
                                        onOpenCurl={openCurlImport}
                                        discovering={discovering}
                                    />
                                ) : (
                                    <div className="text-center">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setAdvancedOpen((prev) => ({ ...prev, [p.platform]: true }))}
                                            className="text-xs text-muted-foreground"
                                            data-testid={`admin-show-advanced-${p.platform}`}
                                        >
                                            Show advanced settings ↓
                                        </Button>
                                    </div>
                                )}
                            </TabsContent>
                        );
                    })}
                </Tabs>
            </Card>

            <DiscoveryDialog
                open={discoverOpen}
                onOpenChange={setDiscoverOpen}
                discovering={discovering}
                result={discoverResult}
                onApply={applyEndpoint}
            />

            <CurlImportDialog
                open={curlOpen}
                onOpenChange={setCurlOpen}
                platform={curlPlatform}
                text={curlText}
                onTextChange={setCurlText}
                inAppId={curlInAppId}
                onInAppIdChange={setCurlInAppId}
                amount={curlAmount}
                onAmountChange={setCurlAmount}
                importing={curlImporting}
                result={curlResult}
                onSubmit={importCurl}
            />
        </AdminShell>
    );
}
